import React from 'react';
import { Typography } from 'antd';
import { FctEstado } from '../../fct/react';
import { fct } from '../../fct/tokens/tokens';
import type { RespuestaEncuesta } from '../../api/cxPanelApi';
import { notaNps } from './panelCx';
import { ESCALA, leerCuestionarioCompra } from './cuestionarioCompra';

const { Text } = Typography;

/*
 * Respuestas de una ROC, OC o SI con el cuestionario que ve el cliente en Typeform (cuestionarioCompra.ts):
 * enunciados literales, en su orden, cada pregunta una vez. El NPS va como chip de color del semáforo del manual
 * (9-10 éxito, 7-8 aviso, 0-6 riesgo); las valoraciones, con las estrellas del formulario; el comentario, como cita.
 */

/** Nota del NPS como chip del semáforo del manual (color + icono + etiqueta). Si no es una nota de 0 a 10, el valor tal cual. */
export const NpsChip: React.FC<{ valor: string }> = ({ valor }) => {
  const nps = notaNps(valor);
  if (!nps) return <span>{valor}</span>;
  return (
    <span title={`NPS ${nps.nota} de 10 · ${nps.categoria.toLowerCase()}`} data-familia={nps.familia}>
      <FctEstado personalizado={{ etiqueta: `${nps.nota} · ${nps.categoria}`, familia: nps.familia, icono: nps.icono }} />
    </span>
  );
};

/** Valoración de 1 a 5 con las estrellas del formulario y la nota al lado. */
const Estrellas: React.FC<{ valor: string }> = ({ valor }) => {
  const nota = Math.round(Number(valor));
  const max = ESCALA.estrellas.max;
  return (
    <span role="img" aria-label={`${nota} de ${max}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
      <span aria-hidden="true" style={{ fontSize: 18, letterSpacing: 2, lineHeight: 1 }}>
        {Array.from({ length: max }, (_, i) => (
          <span key={i} style={{ color: i < nota ? fct.marca['verde-oscuro'] : fct.linea.tarjeta }}>★</span>
        ))}
      </span>
      <span className="fct-mono" style={{ color: fct.tinta['700'], fontSize: 13 }}>{nota} / {max}</span>
    </span>
  );
};

const RespuestasCompra: React.FC<{
  tipo: string;
  respuestas: RespuestaEncuesta[];
  /** Cómo pintar las respuestas que no son del cuestionario (si las hay). */
  otras: (r: RespuestaEncuesta[]) => React.ReactNode;
}> = ({ tipo, respuestas, otras }) => {
  const lectura = leerCuestionarioCompra(tipo, respuestas);
  if (!lectura) return <>{otras(respuestas)}</>;

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <section style={{ border: `1px solid ${fct.linea.tarjeta}`, borderRadius: 8, padding: '4px 16px', background: fct.superficie.tarjeta }}>
        {lectura.respuestas.map(({ pregunta, valor }, i) => {
          const numero = (
            <span className="fct-mono" style={{ fontWeight: 700, color: fct.marca['verde-oscuro'], marginInlineEnd: 10 }}>{i + 1}</span>
          );
          const borde = i === 0 ? undefined : `1px solid ${fct.linea.fila}`;
          if (pregunta.tipo === 'texto') {
            return (
              <div key={pregunta.clave} style={{ padding: '12px 0', borderTop: borde }}>
                <div style={{ color: fct.tinta['900'], marginBottom: 6 }}>{numero}{pregunta.texto}</div>
                {valor
                  ? <div style={{ padding: '8px 12px', background: fct.superficie.hundida, borderRadius: 6, whiteSpace: 'pre-wrap' }}>{valor}</div>
                  : <Text type="secondary">Sin comentario</Text>}
              </div>
            );
          }
          return (
            <div
              key={pregunta.clave}
              style={{
                display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 16, alignItems: 'center',
                padding: '12px 0', borderTop: borde,
              }}
            >
              <div style={{ color: fct.tinta['900'] }}>
                {numero}
                {pregunta.tipo === 'nps' && (
                  <span style={{
                    fontSize: 11, fontWeight: 700, letterSpacing: '.06em', color: fct.tinta['500'], marginInlineEnd: 8,
                  }}
                  >
                    NPS
                  </span>
                )}
                {pregunta.texto}
              </div>
              <div>
                {valor == null && <Text type="secondary">Sin contestar</Text>}
                {valor != null && pregunta.tipo === 'nps' && <NpsChip valor={valor} />}
                {valor != null && pregunta.tipo === 'estrellas' && <Estrellas valor={valor} />}
              </div>
            </div>
          );
        })}
      </section>
      {lectura.otras.length > 0 && (
        <section>
          <div style={{ fontWeight: 600, color: fct.tinta['700'], margin: '4px 0 8px' }}>Otras respuestas de la encuesta</div>
          {otras(lectura.otras)}
        </section>
      )}
    </div>
  );
};

export default RespuestasCompra;
